import { Box } from "@mui/material";
import React, { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { getBarColour } from "../../../../../store/helper";
import { formatClockTime } from "../../helpers/activitySelectors";
import { TrainingZoneObj } from "../../model/workoutPlanTypes";

type Props = {
  samples: Array<{ timestamp: string; bpm: number }>;
  zones: TrainingZoneObj[];
};

const CHART_HEIGHT = 220;
const GRADIENT_ID = "workout-hr-zone-gradient";

/**
 * The mobile BPM line chart: the heart-rate series over the session, the
 * line coloured by the training zone each beat falls in (a vertical gradient
 * with one stop per zone ceiling, zone colours from `getBarColour`), the
 * y-axis starting five beats under the lowest reading.
 */
const HeartRateChart = ({ samples, zones }: Props) => {
  const theme: Theme = useTheme();

  const data = useMemo(
    () =>
      samples.map((sample) => ({
        time: formatClockTime(sample.timestamp),
        bpm: sample.bpm,
      })),
    [samples]
  );

  const { lower, upper } = useMemo(() => {
    const values = samples.map((sample) => sample.bpm);
    const max = values.length ? Math.max(...values) : 0;
    const min = values.length ? Math.min(...values) : 0;
    return { lower: Math.max(0, min - 5), upper: max };
  }, [samples]);

  // Gradient stops: from the top of the chart (upper) down to the bottom
  // (lower), the colour switches at each zone's ceiling.
  const stops = useMemo(() => {
    const range = Math.max(1, upper - lower);
    const sorted = [...zones].sort((a, b) => b.zone - a.zone);
    const result: Array<{ offset: number; color: string }> = [];
    sorted.forEach((zone, index) => {
      const color = getBarColour(zone.zone - 1, 5, "workoutAnalysis", theme);
      const ceiling = zone.zone === 5 ? upper : Math.min(upper, zone.range.max_value);
      const floor = index === sorted.length - 1 ? lower : Math.max(lower, zone.range.min_value);
      const top = Math.max(0, Math.min(1, (upper - ceiling) / range));
      const bottom = Math.max(0, Math.min(1, (upper - floor) / range));
      if (bottom > top) {
        result.push({ offset: top, color }, { offset: bottom, color });
      }
    });
    return result.length ? result : [{ offset: 0, color: theme.colorTokens.S[30] }, { offset: 1, color: theme.colorTokens.S[30] }];
  }, [zones, lower, upper, theme]);

  if (data.length === 0) return null;

  // Roughly five time labels across the series, as the mobile chart shows.
  const labelEvery = Math.max(1, Math.floor(data.length / 5));

  return (
    <Box sx={styles.container}>
      <Box sx={styles.title(theme)}>BPM</Box>
      <Box sx={{ width: "100%", height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <defs>
              <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                {stops.map((stop, index) => (
                  <stop key={index} offset={stop.offset} stopColor={stop.color} />
                ))}
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={theme.colors_ds.NEUTRAL_BORDER_WEAK} />
            <XAxis
              dataKey="time"
              interval={labelEvery - 1}
              tick={{ fontSize: 12, fill: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
              axisLine={{ stroke: theme.colors_ds.NEUTRAL_BORDER_DEFAULT }}
              tickLine={false}
            />
            <YAxis
              domain={[lower, upper]}
              tick={{ fontSize: 12, fill: theme.colors_ds.NEUTRAL_TEXT_WEAK }}
              axisLine={{ stroke: theme.colors_ds.NEUTRAL_BORDER_DEFAULT }}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value: number) => [`${value} bpm`, ""]}
              separator=""
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
                fontFamily: "var(--font-Medium)",
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="bpm"
              stroke={`url(#${GRADIENT_ID})`}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: theme.colors_ds.PRIMARY_ICON_MAIN, strokeWidth: 0 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default HeartRateChart;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
};
